import React, { createContext, useContext, useMemo } from "react";
import dataService from "../services/data.service";
import { getTpxPermissions } from "../permissions/tpxUserPermissions";

/**
 * React context carrying the effective TPX permission snapshot for the
 * currently-loaded user. The provider MUST be rendered only after
 * `dataService.fetchUserMeta(...)` has resolved — see `App.js`, which
 * already gates rendering of the router on `isUserDataLoading === false`.
 *
 * Default value is the "no permissions" snapshot, which fails closed.
 */
const DEFAULT_SNAPSHOT = Object.freeze({
  tpxRoles: [],
  tpxPermissions: [],
  planView: false,
  planEdit: false,
  planCreate: false,
  planDelete: false,
  versionCreate: false,
  settingsView: false,
  settingsEdit: false,
  coaView: false,
  coaEdit: false,
  planningReportView: false,
  planningReportEdit: false,
  critiqueView: false,
  critiqueComment: false,
  critiqueSubmit: false,
  critiqueDelete: false,
  critiqueTemplateView: false,
  critiqueTemplateCreate: false,
  critiqueTemplateEdit: false,
  critiqueTemplateDelete: false,
  lockPlan: false,
  lockLine: false,
  lockDriver: false,
});

const TpxPermissionsContext = createContext(DEFAULT_SNAPSHOT);

export const TpxPermissionsProvider = ({ children, user }) => {
  // Derive from the user payload if provided; otherwise fall back to the
  // singleton `dataService.user` so consumers can still work in code paths
  // where the provider is mounted without an explicit `user` prop.
  const value = useMemo(() => {
    const source = user || dataService.user || null;
    return getTpxPermissions(source);
  }, [user]);

  return (
    <TpxPermissionsContext.Provider value={value}>
      {children}
    </TpxPermissionsContext.Provider>
  );
};

/**
 * Read the current TPX permission snapshot.
 * Always returns a fully-populated object (never undefined), so callers can
 * destructure safely: `const { planView, planEdit } = useTpxPermissions();`
 */
export const useTpxPermissions = () => useContext(TpxPermissionsContext);

export default TpxPermissionsContext;
